import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiGet, apiPost } from 'api-service';
import {
  getContentRequestQAndQueryId,
  getEnv,
  getRequestTimeDuration,
  mapApiPlatformToAdapted,
  mapToApiPlatform,
} from 'utils';
import translation from 'translation';
import { toast } from 'lib';
import { getContentPayload } from './contentHelpers';
import { contentAdapter } from './adapters';

const { t } = translation;
export const fetchContents = createAsyncThunk(
  'contentReducer/fetchContents',
  async ({ platform, params }, thunkAPI) => {
    const payload = getContentPayload(platform, thunkAPI);
    const { api_path: apiPath } = payload;

    try {
      /**
       * if track_total is 1 and also api_path is /count, then api send only total
       * caution: if only track_total is 1 and /count is not in api_path,
       * then  api returns does'nt return any contents and also does'nt return total at all. please be careful.
       * by default track_total is 1
       *  */
      const response = await apiGet('/search/api/v1/search', {
        ...payload,
        ...params,
        api_path: params?.track_total === 1 ? `${apiPath}/count` : apiPath,
        page:
          params?.page ||
          thunkAPI.getState().contentReducer.contents[platform].pageNumber,
      });
      const platformContents = {
        contents:
          response?.data?.result?.data?.map((content) => {
            return contentAdapter(
              content,
              mapApiPlatformToAdapted(content.platform) || platform
            );
          }) || [],
        total: response.data.result?.meta?.total,
      };

      if (params?.page === 1) {
        platformContents.pageNumber = 1;
      }

      return platformContents;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchPlatformsContentCount = createAsyncThunk(
  'contentReducer/fetchPlatformsContentCount',
  async ({ platformsPermission }, thunkAPI) => {
    try {
      for (const platform in platformsPermission) {
        const hasPermission = platformsPermission[platform];
        if (hasPermission) {
          thunkAPI.dispatch(
            fetchContents({
              platform,
              params: {
                track_total: 1,
              },
            })
          );
        }
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const reportUnrelatedPost = createAsyncThunk(
  'contentReducer/reportUnrelatedPost',

  async ({ platform, params }, thunkAPI) => {
    const { queryId } = getContentRequestQAndQueryId({
      currentQuery: thunkAPI.getState().pageReducer.currentQuery,
    });
    try {
      const response = await apiPost('/search/api/v1/unrelated-posts', {
        platform,
        ...params,
        query_id: queryId,
      });

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updatePostSentiment = createAsyncThunk(
  'contentReducer/updatePostSentiment',
  async ({ params }, thunkAPI) => {
    const { queryId } = getContentRequestQAndQueryId({
      currentQuery: thunkAPI.getState().pageReducer.currentQuery,
    });
    const { currentDate } = thunkAPI.getState().pageReducer;

    const defaultParams = {
      from: currentDate.timeDuration ? undefined : currentDate.fromDate,
      to: currentDate.timeDuration ? undefined : currentDate.toDate,
      time_duration: getRequestTimeDuration(currentDate),
      ...params,
    };

    try {
      const response = await apiPost('/search/api/v1/user-sentiment', {
        ...defaultParams,
        query_id: queryId,
      });

      toast({
        title: t('content:success.updatePostSentiment.title'),
        description: t('content:success.updatePostSentiment.description'),
        type: 'success',
      });

      return response.data;
    } catch (error) {
      toast({
        title: t('content:error.updatePostSentiment.title'),
        description: t('content:error.updatePostSentiment.description'),
        type: 'error',
      });
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchTwitterAggregateInfo = createAsyncThunk(
  'contentReducer/fetchTwitterAggregateInfo',
  async (_, thunkAPI) => {
    const payload = getContentPayload('twitter', thunkAPI);

    try {
      const response = await apiGet('/search/api/v1/search', {
        ...payload,
        api_path: '/twitter/statistics',
      });
      return response?.data?.result?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const markPostAsSeen = createAsyncThunk(
  'contentReducer/markPostAsSeen',
  async ({ postId, platform }, { rejectWithValue }) => {
    const apiPlatform = mapToApiPlatform[platform] || platform;

    const post = { post_id: postId, platform: apiPlatform };

    try {
      await apiPost(`${getEnv('REACT_APP_BASE_API')}/peeky/api/v1/seen/batch`, {
        posts: [post],
      });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
